import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { useWorkspaces } from './useWorkspaces';
import { useAuth } from './useAuth';
import { supabase } from '@/lib/supabase';
import { useToast } from './use-toast';
import { queryKeys, mutationKeys } from '@/lib/query-keys';
import { captureError } from '@/lib/sentry';

export interface CalendarCalendar {
  id: string;
  provider_id: string | null;
  workspace_id: string;
  user_id: string;
  external_id: string;
  name: string;
  color?: string;
  is_primary: boolean;
  is_selected: boolean;
  access_role?: string;
  // Joined from email_providers (null for the local "Buildit" calendar)
  provider?: { type: string; email: string | null; display_name: string | null } | null;
}

export interface CalendarEvent {
  id: string;
  calendar_id: string;
  workspace_id: string;
  user_id: string;
  external_id?: string;
  title: string;
  description?: string;
  start_time: string;
  end_time?: string;
  all_day: boolean;
  location?: string;
  attendees?: { email: string; name?: string; response_status?: string }[];
  status: 'confirmed' | 'tentative' | 'cancelled';
  source: 'synced' | 'local';
}

// --- Query hooks ---

export function useCalendarCalendars(providerId?: string) {
  const { currentWorkspace } = useWorkspaces();

  return useQuery({
    queryKey: queryKeys.calendarCalendars.list(currentWorkspace?.id, providerId),
    queryFn: async () => {
      if (!currentWorkspace?.id) return [];

      let query = supabase
        .from('calendar_calendars')
        .select('*, provider:email_providers(type, email, display_name)')
        .eq('workspace_id', currentWorkspace.id)
        .order('is_primary', { ascending: false })
        .order('name');

      if (providerId) {
        query = query.eq('provider_id', providerId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as CalendarCalendar[];
    },
    enabled: !!currentWorkspace?.id,
  });
}

export function useEnsureLocalCalendar() {
  const { currentWorkspace } = useWorkspaces();
  const queryClient = useQueryClient();

  return useCallback(async (workspaceId?: string): Promise<CalendarCalendar | null> => {
    const wsId = workspaceId ?? currentWorkspace?.id;
    if (!wsId) return null;
    try {
      const { data, error } = await supabase.rpc('ensure_local_calendar', {
        p_workspace_id: wsId,
      });
      if (error) throw error;
      await queryClient.invalidateQueries({ queryKey: queryKeys.calendarCalendars.all });
      return data as CalendarCalendar;
    } catch (err) {
      captureError(err as Error, { feature: 'calendar-event-create' });
      return null;
    }
  }, [currentWorkspace?.id, queryClient]);
}

export function useCalendarEvents(dateRange?: { start: string; end: string }) {
  const { currentWorkspace } = useWorkspaces();

  return useQuery({
    queryKey: queryKeys.calendarEvents.list(currentWorkspace?.id, dateRange),
    queryFn: async () => {
      if (!currentWorkspace?.id) return [];

      let query = supabase
        .from('calendar_events')
        .select(`
          *,
          calendar:calendar_calendars!inner(
            id, name, color, is_selected, provider_id
          )
        `)
        .eq('workspace_id', currentWorkspace.id)
        .eq('calendar.is_selected', true)
        .order('start_time');

      if (dateRange) {
        query = query
          .gte('start_time', dateRange.start)
          .lte('start_time', dateRange.end);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as (CalendarEvent & { calendar: Pick<CalendarCalendar, 'id' | 'name' | 'color' | 'is_selected' | 'provider_id'> })[];
    },
    enabled: !!currentWorkspace?.id,
  });
}

// --- Mutation hooks ---

export function useSyncCalendar() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationKey: mutationKeys.syncCalendar,
    mutationFn: async (providerId: string) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/calendar-sync`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ provider_id: providerId }),
        },
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Sync failed' }));
        throw new Error(error.error || 'Sync failed');
      }

      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.calendarEvents.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.calendarCalendars.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.emailSettings.all });
      const total = (data.events_added || 0) + (data.events_updated || 0);
      toast({
        description: total > 0
          ? `Synced ${total} event${total !== 1 ? 's' : ''}`
          : 'Calendar is up to date',
      });
    },
    onError: (error) => {
      console.error('Sync error:', error);
      captureError(error as Error, { feature: 'calendar-sync' });
      toast({
        title: "Sync failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useToggleCalendar() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: mutationKeys.toggleCalendar,
    mutationFn: async ({ calendarId, isSelected }: { calendarId: string; isSelected: boolean }) => {
      const { error } = await supabase
        .from('calendar_calendars')
        .update({ is_selected: isSelected })
        .eq('id', calendarId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.calendarCalendars.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.calendarEvents.all });
    },
  });
}

export function useCreateCalendarEvent() {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useWorkspaces();
  const { user } = useAuth();
  const { toast } = useToast();

  return useMutation({
    mutationKey: mutationKeys.createEvent,
    mutationFn: async (event: {
      calendar_id: string;
      title: string;
      description?: string;
      start_time: string;
      end_time?: string;
      all_day?: boolean;
      location?: string;
    }) => {
      if (!currentWorkspace?.id || !user?.id) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('calendar_events')
        .insert({
          ...event,
          workspace_id: currentWorkspace.id,
          user_id: user.id,
          source: 'local',
          status: 'confirmed',
        })
        .select()
        .single();

      if (error) throw error;

      // Push to provider via edge function (fire-and-forget)
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          await fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/calendar-create-event`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${session.access_token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ event_id: data.id }),
            },
          );
        }
      } catch (pushError) {
        console.warn('Failed to push event to provider:', pushError);
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.calendarEvents.all });
      toast({ description: "Event created" });
    },
    onError: (error) => {
      console.error('Create event error:', error);
      captureError(error as Error, { feature: 'calendar-event-create' });
      toast({
        title: "Error",
        description: "Failed to create event",
        variant: "destructive",
      });
    },
  });
}

export function useUpdateCalendarEvent() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationKey: mutationKeys.updateEvent,
    mutationFn: async (event: {
      id: string;
      calendar_id: string;
      title: string;
      description?: string;
      start_time: string;
      end_time?: string;
      all_day?: boolean;
      location?: string;
    }) => {
      const { data, error } = await supabase
        .from('calendar_events')
        .update({
          calendar_id: event.calendar_id,
          title: event.title,
          description: event.description ?? null,
          start_time: event.start_time,
          end_time: event.end_time ?? null,
          all_day: event.all_day ?? false,
          location: event.location ?? null,
        })
        .eq('id', event.id)
        .select()
        .single();

      if (error) throw error;

      // NOTE: provider-side propagation (PATCH to Google/Outlook) is a follow-up —
      // there is no calendar-update-event edge function yet. Local "Buildit" events
      // update completely here; a synced provider event updates in BuildIt but the
      // change is not yet pushed to the external calendar (and may be overwritten on
      // the next sync). We intentionally do NOT call calendar-create-event, which
      // would create a duplicate on the provider (the bug this fix removes locally).
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.calendarEvents.all });
      toast({ description: "Event updated" });
    },
    onError: (error) => {
      console.error('Update event error:', error);
      captureError(error as Error, { feature: 'calendar-event-update' });
      toast({
        title: "Error",
        description: "Failed to update event",
        variant: "destructive",
      });
    },
  });
}

export function useDeleteCalendarEvent() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationKey: mutationKeys.deleteEvent,
    mutationFn: async (eventId: string) => {
      const { error } = await supabase
        .from('calendar_events')
        .delete()
        .eq('id', eventId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.calendarEvents.all });
      toast({ description: "Event deleted" });
    },
    onError: (error) => {
      console.error('Delete event error:', error);
      captureError(error as Error, { feature: 'calendar-event-delete' });
      toast({
        title: "Error",
        description: "Failed to delete event",
        variant: "destructive",
      });
    },
  });
}

// --- Auto-sync hook (silent, rate-limited) ---

const SYNC_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

export function useAutoSyncCalendar() {
  const { currentWorkspace } = useWorkspaces();
  const queryClient = useQueryClient();
  const lastSyncRef = useRef<number>(0);
  const syncingRef = useRef(false);

  useEffect(() => {
    if (!currentWorkspace?.id) return;

    const syncAll = async () => {
      // Prevent overlapping syncs
      if (syncingRef.current) return;
      // Rate limit
      if (Date.now() - lastSyncRef.current < SYNC_INTERVAL_MS) return;

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // Get unique provider IDs that have calendars in this workspace
      const { data: calendars } = await supabase
        .from('calendar_calendars')
        .select('provider_id')
        .eq('workspace_id', currentWorkspace.id);

      if (!calendars?.length) return;

      const providerIds = [...new Set(calendars.map(c => c.provider_id).filter(Boolean))] as string[];

      syncingRef.current = true;
      lastSyncRef.current = Date.now();

      try {
        for (const providerId of providerIds) {
          try {
            await fetch(
              `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/calendar-sync`,
              {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${session.access_token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ provider_id: providerId }),
              },
            );
          } catch {
            // Silently ignore individual sync failures
          }
        }

        // Refresh calendar data in the UI
        queryClient.invalidateQueries({ queryKey: queryKeys.calendarEvents.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.calendarCalendars.all });
      } finally {
        syncingRef.current = false;
      }
    };

    // Sync on mount
    syncAll();

    // Sync periodically
    const interval = setInterval(syncAll, SYNC_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [currentWorkspace?.id, queryClient]);
}
